import React from 'react';
import styled from '@emotion/styled';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  typography,
} from '../../style-utils/theme';
import { SCALE } from '../../motion';

/**
 * MINUS / VALUE / PLUS — a small count, changed a step at a time.
 *
 * For numbers a person nudges rather than types: a party size, a guest count.
 * `number-of-people.tsx` uses a free number input for this today, which on a
 * phone means opening a keyboard to change 1 to 2.
 *
 * Every button is a 44px target at every pointer. The value sits in a polite
 * live region, so a screen-reader user who presses "More guests" hears the new
 * number without having to go looking for it.
 *
 * AT A BOUND THE BUTTON SAYS WHY. "Fewer guests — at least 1" is its accessible
 * name there, not just "Fewer guests, dimmed", which leaves the listener to
 * guess whether the control is broken.
 *
 * aria-disabled, NOT the disabled attribute, for the bound. A natively disabled
 * button drops focus the moment it is disabled — so the keyboard user who
 * presses "More" up to the maximum would find their focus thrown back to the
 * top of the page on the very press that reached it. aria-disabled announces
 * the same "unavailable" state, keeps focus where it was, and the click is
 * refused in the handler. The `disabled` PROP (the whole control is off, e.g.
 * while the form submits) still uses the real attribute.
 */

export interface StepperProps {
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  /** The counted noun, plural and lower-case: "guests" names the buttons
   *  "Fewer guests" and "More guests". */
  label: string;
  /** How far one press moves the value (default 1): a minutes field steps by 5. */
  step?: number;
  /** The group's accessible name when it should differ from the capitalised
   *  label (a field whose visible label is a full phrase). */
  groupLabel?: string;
  disabled?: boolean;
  className?: string;
}

const capitalise = (text: string): string =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

export const Stepper: React.FC<StepperProps> = ({
  value,
  min,
  max,
  onChange,
  label,
  step: stepBy = 1,
  groupLabel,
  disabled = false,
  className,
}) => {
  const atMin = value <= min;
  const atMax = value >= max;
  const fewerName = atMin ? `Fewer ${label} — at least ${min}` : `Fewer ${label}`;
  const moreName = atMax ? `More ${label} — at most ${max}` : `More ${label}`;

  const step = (delta: number) => {
    const next = Math.min(max, Math.max(min, value + delta * stepBy));

    if (next !== value) onChange(next);
  };

  return (
    <Wrap role="group" aria-label={groupLabel || capitalise(label)} className={className}>
      <StepButton
        type="button"
        aria-label={fewerName}
        aria-disabled={atMin || undefined}
        disabled={disabled}
        onClick={() => {
          if (!atMin) step(-1);
        }}
      >
        <span aria-hidden="true">−</span>
      </StepButton>
      <Value aria-live="polite" aria-atomic="true">
        {value}
      </Value>
      <StepButton
        type="button"
        aria-label={moreName}
        aria-disabled={atMax || undefined}
        disabled={disabled}
        onClick={() => {
          if (!atMax) step(1);
        }}
      >
        <span aria-hidden="true">+</span>
      </StepButton>
    </Wrap>
  );
};

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
`;

const StepButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  padding: 0;
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.pill};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  line-height: 1;
  cursor: pointer;
  transition: color ${duration.fast} ${easing.standard},
    background-color ${duration.fast} ${easing.standard},
    border-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.base} ${easing.standard},
    transform ${duration.instant} ${easing.spring};

  &:hover:not(:disabled):not([aria-disabled='true']) {
    border-color: ${palette.lagoon};
    background: ${palette.irisTintSoft};
    transform: translateY(-1px);
  }

  &:active:not(:disabled):not([aria-disabled='true']) {
    transform: scale(${SCALE.pressed});
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled,
  &[aria-disabled='true'] {
    cursor: not-allowed;
    color: ${palette.slateDisabled};
    background: ${palette.surfaceMuted};
    border-color: ${palette.line};
  }
`;

const Value = styled.span`
  min-width: ${spacing.xl};
  text-align: center;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  font-variant-numeric: tabular-nums;
  color: ${palette.ink};
`;
